import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import axiosInstance from '@libs/axios';
import { apiUrl } from '@constants/url';
import type { LanguageCode } from '@constants/translations';

interface ApiEnvelope<T> {
  success: boolean;
  data: T;
  message?: string;
}

/**
 * `null` means the user has never chosen — not "chose Indonesian", not "chose
 * off". Whoever reads a null substitutes their own default, which is what lets
 * a company-level default still reach people who never opened Pengaturan.
 */
export interface UserPreferences {
  language: LanguageCode | null;
  /** Automatic document-type detection on the upload screen. */
  aiDocDetection: boolean | null;
  /** One of SELECTABLE_THEME_MODES; stored as an opaque string by the server. */
  themeMode: string | null;
  /** Rem scale for the app: 1, 1.1 or 1.2. */
  textScale: 1 | 1.1 | 1.2 | null;
}

/** A save mentions only the preferences it means to change. */
export type UserPreferencesPatch = Partial<UserPreferences>;

export const userPreferenceQueryKeys = {
  me: ['user', 'preferences'] as const,
};

const isLanguageCode = (value: unknown): value is LanguageCode => value === 'id' || value === 'en' || value === 'zh';

const isTextScale = (value: unknown): value is 1 | 1.1 | 1.2 => value === 1 || value === 1.1 || value === 1.2;

const EMPTY_PREFERENCES: UserPreferences = { language: null, aiDocDetection: null, themeMode: null, textScale: null };

/** Tolerates any server shape: a missing or foreign value reads as "never chose". */
const parsePreferences = (data: Partial<UserPreferences> | undefined): UserPreferences => ({
  language: isLanguageCode(data?.language) ? data.language : null,
  aiDocDetection: typeof data?.aiDocDetection === 'boolean' ? data.aiDocDetection : null,
  themeMode: typeof data?.themeMode === 'string' && data.themeMode !== '' ? data.themeMode : null,
  textScale: isTextScale(data?.textScale) ? data.textScale : null,
});

/**
 * The signed-in user's stored UI preferences (Issue 8).
 *
 * Fails SILENTLY to "nothing stored": the language already works from
 * localStorage, so a broken or not-yet-deployed endpoint must never leave the
 * app without a language — it only stops following the user across devices.
 * Detection reads the same way: nothing stored means the company default
 * decides, which is the behaviour that shipped before the preference existed.
 */
export function useUserPreferences(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: userPreferenceQueryKeys.me,
    queryFn: async ({ signal }): Promise<UserPreferences> => {
      try {
        const response = await axiosInstance.get<ApiEnvelope<UserPreferences>>(apiUrl.users.mePreferences, {
          signal,
        });
        return parsePreferences(response.data?.data);
      } catch (error) {
        if (axios.isCancel(error)) {
          throw error;
        }
        console.warn('[preferences] fetch failed; keeping the local defaults', error);
        return EMPTY_PREFERENCES;
      }
    },
    staleTime: 30 * 60 * 1000,
    retry: 1,
    enabled: options?.enabled ?? true,
  });
}

/**
 * Writes preferences back to the profile so the choices survive a new device.
 *
 * Takes a patch rather than a whole record: the endpoint leaves out what the
 * body leaves out, so saving the language can never reset a detection
 * preference written by an older or newer build of this page.
 */
export function useUpdateUserPreferences() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (patch: UserPreferencesPatch): Promise<UserPreferences> => {
      const response = await axiosInstance.put<ApiEnvelope<UserPreferences>>(apiUrl.users.mePreferences, patch);
      return parsePreferences(response.data?.data);
    },
    onSuccess: (data) => {
      queryClient.setQueryData(userPreferenceQueryKeys.me, data);
    },
    onError: (error) => {
      // The local switch already happened and localStorage already holds it;
      // only the cross-device part is lost, which is not worth a toast.
      console.warn('[preferences] not saved to the profile', error);
    },
  });
}
